import gql from 'graphql-tag';

import {assert} from 'Asserts';
import {ContextMenuOption, ContextMenuDisplayGroup} from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as LookupColumnsDAO from 'notebook/structured-tables/lookup-columns/LookupColumnsDAO';
import {
  DedicatedLookupStructuredTableColumn,
  LookupConfig,
} from 'notebook/structured-tables/StructuredTableConstants';
import {handleRefreshLookupColumns} from 'notebook/tables/refresh-table-data/RefreshLookupColumnData';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  SelectionRange,
  StructuredTableToTableMetadata,
  Table,
  TableColumnType,
} from 'notebook/tables/TableConstants';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import notifications from 'notifications';
import {omit} from 'util/omitKey';
import {withLoadingScreen} from 'util/Promises';

import {logColumnAnalytics} from '../util/StructuredTableAnalyticsUtil';
import {GetLookupConfigQuery, GetLookupConfigQueryVariables} from './__generated/GetLookupConfigQuery';
import {
  createLookupColumnFromConfig,
  NotebookColumnInfo,
  runCreateLookupColumnConfigModal,
  runEditLookupConfigModal,
} from './runLookupConfigModalForNotebook';

export const EDIT_LOOKUP_COLUMN_NAME = 'Edit lookup column';
export const INSERT_LOOKUP_COLUMN_LEFT_NAME = 'Insert lookup column left';
export const INSERT_LOOKUP_COLUMN_RIGHT_NAME = 'Insert lookup column right';
export const REFRESH_LOOKUP_COLUMN_NAME = 'Refresh lookup column';
export const DUPLICATE_LOOKUP_COLUMN_LEFT_NAME = 'Duplicate lookup column left';
export const DUPLICATE_LOOKUP_COLUMN_RIGHT_NAME = 'Duplicate lookup column right';

const GET_LOOKUP_CONFIG_QUERY = gql`
  query GetLookupConfigQuery($lookupConfigId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    lookupConfig(id: $lookupConfigId) {
      ... on LookupConfigNode {
        id
        isMulti
        multiFlattenLevel
        lookupSteps
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Define menu options for inserting lookup columns.
 *
 * These menu options will be part of each table column's context menu.
 */
export default function getLookupColumnMenuOptions(
  tableName: string,
  table: Table,
  tableIsLocked: boolean,
  selectionRange: SelectionRange | null,
  insertColumnsLeft: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {}
  ) => void,
  insertColumnsRight: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {}
  ) => void,
  updateColumnConstraint: (columnIndex: number, columnConstraint: ColumnConstraint) => void,
  fileAnalyticsParams: FileAnalyticsParams,
  notebookColumnInfos: Array<NotebookColumnInfo>
): Array<ContextMenuOption> {
  const tableMetadata = table.metadata as StructuredTableToTableMetadata<unknown>;

  const structuredTableAnalyticsParams = {
    structuredTableKind: tableMetadata.kind,
    structuredTableProvenanceUuid: tableMetadata.provenanceUuid,
  };

  // Show edit / refresh / duplicate lookup column menu options iff:
  // - One column is selected
  // - The selected column is not protected (i.e. locked)
  // - The selected column is a lookup column
  const shouldShowSingleLookupColumnSelectionOptions = (): boolean => {
    if (selectionRange != null && selectionRange.minIndex === selectionRange.maxIndex) {
      const columnConstraint = table.columnConstraints[selectionRange.minIndex];
      if (columnConstraint != null) {
        const {structuredTableColumn} = columnConstraint;
        return (
          structuredTableColumn?.columnType === TableColumnType.DEDICATED_LOOKUP &&
          !structuredTableColumn.isProtected
        );
      }
    }
    return false;
  };

  const getSelectedLookupColumn = (
    selectionRange: SelectionRange | null
  ): DedicatedLookupStructuredTableColumn => {
    assert(selectionRange != null, 'Menu option should be disabled if no column is selected');
    const selectedLookupColumnIdx = selectionRange.minIndex;
    const selectedLookupColumnConstraint = table.columnConstraints[selectedLookupColumnIdx]!;
    const selectedLookupColumn =
      selectedLookupColumnConstraint.structuredTableColumn as DedicatedLookupStructuredTableColumn;
    return selectedLookupColumn;
  };

  const refreshLookupColumn = (selectionRange: SelectionRange | null): void => {
    const selectedLookupColumn = getSelectedLookupColumn(selectionRange);
    assert(Boolean(fileAnalyticsParams.fileId), 'Menu option should be disabled if no entry ID is defined');
    assert(
      Boolean(table.provenanceUuid),
      'Menu option should be disabled if no table provenance UUID is defined'
    );
    handleRefreshLookupColumns({
      entryOrTemplateId: fileAnalyticsParams.fileId!,
      tableProvenanceUuid: table.provenanceUuid!,
      tableOp: {[table.name]: {type: 'eval-columns', colIndexes: [selectionRange!.minIndex]}},
      columns: [selectedLookupColumn],
    });
  };

  async function duplicateColumnOnClick(
    selectionRange: SelectionRange | null,
    insertColumnHandler: (
      tableName: string,
      start: number,
      end: number,
      columnInfo: {} | InsertableColumnInfo
    ) => void
  ): Promise<void> {
    assert(selectionRange != null, 'Menu option should be disabled if no column is selected');
    const selectedLookupColumn = getSelectedLookupColumn(selectionRange);
    const existingLookupConfigId = selectedLookupColumn.lookupConfig.id;

    assert(
      existingLookupConfigId != null,
      'Found null lookupConfig id in column when attempting duplication of lookup column config.'
    );

    const lookupConfig = await withLoadingScreen(
      // oxlint-disable-next-line no-async-promise-executor -- Disabling for now, but worth fixing
      new Promise<LookupConfig>(async (resolve) => {
        const {lookupConfig: lookupConfigSpec} = await doQuerySafe<
          GetLookupConfigQuery,
          GetLookupConfigQueryVariables
        >({query: GET_LOOKUP_CONFIG_QUERY}, {variables: {lookupConfigId: existingLookupConfigId}});

        assert(
          !isInaccessible(lookupConfigSpec),
          'Somehow found inaccessible lookup config spec for a column that a user has access to'
        );

        const {lookupConfig} = await LookupColumnsDAO.createNotebookLookupConfig(
          omit(lookupConfigSpec, '__typename', 'id')
        );

        resolve(lookupConfig);
      })
    );

    const duplicateColumnInfo = createLookupColumnFromConfig({
      lookupConfig,
      finalStepName: `${selectedLookupColumn.name} [Copy]`,
      notebookInputColumnProvenanceUuid: selectedLookupColumn.inputColumnProvenanceUuid,
      unitApiIdentifier: selectedLookupColumn.unitApiIdentifier,
    });
    const duplicateColumn = duplicateColumnInfo.columnConstraints
      ?.structuredTableColumn as DedicatedLookupStructuredTableColumn;
    assert(duplicateColumn != null, 'New column info was incorrectly built when duplicating column');

    logColumnAnalytics({
      analyticsEvent: ELN_ANALYTICS_ACTIONS.DUPLICATE_LOOKUP_COLUMN,
      fileAnalyticsParams,
      structuredTableAnalyticsParams,
      additionalMetadata: {
        oldLookupColumnProvenanceUuid: selectedLookupColumn.provenanceUuid,
        newLookupColumnProvenanceUuid: duplicateColumn.provenanceUuid,
        oldLookupConfigId: existingLookupConfigId,
        newLookupConfigId: duplicateColumn.lookupConfig.id,
        lookupSourceColumnProvenanceUuid: duplicateColumn.inputColumnProvenanceUuid,
      },
    });
    insertColumnHandler(tableName, selectionRange.maxIndex, selectionRange.maxIndex, duplicateColumnInfo);
  }

  async function editColumnOnClick(
    selectionRange: SelectionRange | null,
    table: Table,
    notebookColumnInfos: Array<NotebookColumnInfo>
  ): Promise<void> {
    const selectedLookupColumnIdx = selectionRange!.minIndex;
    const selectedLookupColumnConstraint = table.columnConstraints[selectedLookupColumnIdx]!;
    const selectedLookupColumn =
      selectedLookupColumnConstraint.structuredTableColumn as DedicatedLookupStructuredTableColumn;
    const lookupConfigId = selectedLookupColumn.lookupConfig.id;

    assert(
      lookupConfigId != null,
      'Found null lookupConfig id in column when attempting edit of lookup column config.'
    );
    const {lookupConfig: lookupConfigSpec} = await withLoadingScreen(
      doQuerySafe<GetLookupConfigQuery, GetLookupConfigQueryVariables>(
        {query: GET_LOOKUP_CONFIG_QUERY},
        {variables: {lookupConfigId}}
      )
    );

    const inputColumnInfo = notebookColumnInfos.find(
      (columnInfo: NotebookColumnInfo) =>
        columnInfo.provenanceUuid === selectedLookupColumn.inputColumnProvenanceUuid
    );
    // inputColumnInfo may be null if the input column was deleted from the table after the LUC was initalized.
    // In this case, we want the modal to reset completely, and let the user rebuild the config from scratch.
    let newLookupColumnInfo;
    if (inputColumnInfo != null) {
      assert(
        !isInaccessible(lookupConfigSpec),
        'Somehow found inaccessible lookup config spec for a column that a user has access to'
      );

      newLookupColumnInfo = await runEditLookupConfigModal(
        notebookColumnInfos,
        selectedLookupColumnConstraint,
        inputColumnInfo,
        lookupConfigSpec
      );
    } else {
      // Show toast for user awareness of why the modal reset.
      notifications.warn(
        'The source column for this lookup column does not exist anymore, so the column must be configured from scratch.'
      );
      newLookupColumnInfo = await runEditLookupConfigModal(
        notebookColumnInfos,
        selectedLookupColumnConstraint
      );
    }

    if (newLookupColumnInfo != null) {
      const column = newLookupColumnInfo.columnConstraints
        ?.structuredTableColumn as DedicatedLookupStructuredTableColumn;
      assert(
        column != null,
        'Column info returned by Lookup Column Config modal does not have a properly constructed column constraint.'
      );
      assert(
        column.cid === selectedLookupColumn.cid &&
          column.provenanceUuid === selectedLookupColumn.provenanceUuid,
        'Unintentionally created a new cid or provenance UUID when editing an existing lookup column.'
      );

      logColumnAnalytics({
        analyticsEvent: ELN_ANALYTICS_ACTIONS.EDIT_LOOKUP_COLUMN,
        fileAnalyticsParams,
        structuredTableAnalyticsParams,
        additionalMetadata: {
          lookupColumnProvenanceUuid: selectedLookupColumn.provenanceUuid,
          oldLookupConfigId: lookupConfigId,
          newLookupConfigId: column.lookupConfig.id,
          lookupSourceColumnProvenanceUuid: column.inputColumnProvenanceUuid,
        },
      });
      updateColumnConstraint(selectedLookupColumnIdx, newLookupColumnInfo.columnConstraints!);
    }
  }

  const leftSelectedColumn =
    selectionRange?.minIndex != null ? table.columnConstraints[selectionRange.minIndex] : undefined;
  const rightSelectedColumn =
    selectionRange?.maxIndex != null ? table.columnConstraints[selectionRange.maxIndex] : undefined;

  return [
    ...(shouldShowSingleLookupColumnSelectionOptions()
      ? [
          {
            name: EDIT_LOOKUP_COLUMN_NAME,
            hide: () => tableIsLocked,
            click: async () => {
              await editColumnOnClick(selectionRange, table, notebookColumnInfos);
            },
            displayGroup: ContextMenuDisplayGroup.TOP,
          },
          {
            name: REFRESH_LOOKUP_COLUMN_NAME,
            hide: () => tableIsLocked || !table.provenanceUuid,
            click: () => {
              refreshLookupColumn(selectionRange);
            },
            displayGroup: ContextMenuDisplayGroup.TOP,
          },
          {
            name: DUPLICATE_LOOKUP_COLUMN_LEFT_NAME,
            hide: () => tableIsLocked,
            click: async () => {
              await duplicateColumnOnClick(selectionRange, insertColumnsLeft);
            },
            displayGroup: ContextMenuDisplayGroup.TOP,
          },
          {
            name: DUPLICATE_LOOKUP_COLUMN_RIGHT_NAME,
            hide: () => tableIsLocked,
            click: async () => {
              await duplicateColumnOnClick(selectionRange, insertColumnsRight);
            },
            displayGroup: ContextMenuDisplayGroup.TOP,
          },
        ]
      : []),
    {
      name: INSERT_LOOKUP_COLUMN_LEFT_NAME,
      hide: () => tableIsLocked || Boolean(leftSelectedColumn?.structuredTableColumn?.isSingleInput),
      click: async () => {
        assert(selectionRange != null, 'Menu option should be disabled if no column is selected');
        const lookupColumnInfo = await runCreateLookupColumnConfigModal(notebookColumnInfos);
        if (lookupColumnInfo == null) {
          return;
        }
        const {lookupColumnToInsert} = lookupColumnInfo;
        const columnDefinition = lookupColumnToInsert.columnConstraints?.columnDefinition;
        assert(
          columnDefinition?.type === TableColumnType.DEDICATED_LOOKUP,
          "Column to insert's type should be a lookup column if insert lookup column is clicked"
        );
        logColumnAnalytics({
          analyticsEvent: ELN_ANALYTICS_ACTIONS.INSERT_LOOKUP_COLUMN,
          fileAnalyticsParams,
          structuredTableAnalyticsParams,
          additionalMetadata: {
            lookupColumnProvenanceUuid:
              lookupColumnToInsert.columnConstraints?.structuredTableColumn?.provenanceUuid,
            lookupConfigId: columnDefinition.lookupConfig.id,
            lookupSourceColumnProvenanceUuid: columnDefinition.inputColumnProvenanceUuid,
          },
        });
        insertColumnsLeft(tableName, selectionRange.minIndex, selectionRange.minIndex, lookupColumnToInsert);
      },
      displayGroup: ContextMenuDisplayGroup.MIDDLE,
    },
    {
      name: INSERT_LOOKUP_COLUMN_RIGHT_NAME,
      hide: () => tableIsLocked || Boolean(rightSelectedColumn?.structuredTableColumn?.isSingleInput),
      click: async () => {
        assert(selectionRange != null, 'Menu option should be disabled if no column is selected');
        const lookupColumnInfo = await runCreateLookupColumnConfigModal(notebookColumnInfos);
        if (lookupColumnInfo == null) {
          return;
        }
        const {lookupColumnToInsert} = lookupColumnInfo;
        const columnDefinition = lookupColumnToInsert.columnConstraints?.columnDefinition;
        assert(
          columnDefinition?.type === TableColumnType.DEDICATED_LOOKUP,
          "Column to insert's type should be a lookup column if insert lookup column is clicked"
        );
        logColumnAnalytics({
          analyticsEvent: ELN_ANALYTICS_ACTIONS.INSERT_LOOKUP_COLUMN,
          fileAnalyticsParams,
          structuredTableAnalyticsParams,
          additionalMetadata: {
            lookupColumnProvenanceUuid:
              lookupColumnToInsert.columnConstraints?.structuredTableColumn?.provenanceUuid,
            lookupConfigId: columnDefinition.lookupConfig.id,
            lookupSourceColumnProvenanceUuid: columnDefinition.inputColumnProvenanceUuid,
          },
        });
        insertColumnsRight(tableName, selectionRange.maxIndex, selectionRange.maxIndex, lookupColumnToInsert);
      },
      displayGroup: ContextMenuDisplayGroup.MIDDLE,
      dataTestAttrs: {component: 'ContextMenu', element: 'insertLookupColumnRight'},
    },
  ];
}
